import { useUser } from "@/components/UserProvider";
import {
  AppModal,
  AppPageShell,
  Button,
  Card,
  CardContent,
  ContextMenu,
  ContextMenuItem,
  DataTable,
  Text,
} from "@/components/ui";
import {
  useDeleteMassTortSurvey,
  useMassTortSurveyQuestions,
  useMassTortSurveys,
} from "@/lib/data/react-query/mass-torts/mass-tort-survey";
import type { MassTortSurvey } from "@/lib/data/requests/mass-torts/mass-tort-survey";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { APIError } from "@/lib/types/APIResponse";
import { isAxiosError } from "axios";
import { PlusCircle, Send } from "lucide-react";
import { useState } from "react";
import { DistributionPanel } from "./MassTortSurveyDistributionPanel";
import { MassTortSurveyDetailsModal } from "./MassTortSurveyForms";
import { massTortSurveyTableColumns } from "./mass-tort-survey-table-columns";

function requestError(cause: unknown, fallback: string) {
  return isAxiosError<APIError>(cause)
    ? (cause.response?.data.meta.userMsg ?? fallback)
    : fallback;
}

export function MassTortSurveysView({ massTortId }: { massTortId: string }) {
  const { hasActionPermission } = useUser();
  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTortSurvey,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTortSurvey,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTortSurvey,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canDistribute = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSurvey,
    ACTION_PERMISSION_VALUES.create,
  );
  const [page, setPage] = useState(1);
  const surveys = useMassTortSurveys(massTortId, page);
  const [countBeforeCreate, setCountBeforeCreate] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const selected = surveys.data?.data.find(
    (survey) => survey.id === selectedId,
  );
  const questions = useMassTortSurveyQuestions(selected?.id ?? "");
  const [distributionOpen, setDistributionOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const deleteSurvey = useDeleteMassTortSurvey(massTortId);

  const openSurvey = (survey: MassTortSurvey) => {
    setSelectedId(survey.id);
    setDistributionOpen(false);
    setEditingDetails(true);
    setDetailsOpen(true);
  };

  const openDistribution = (survey: MassTortSurvey) => {
    setSelectedId(survey.id);
    setDetailsOpen(false);
    setDistributionOpen(true);
  };

  const confirmDelete = (survey: MassTortSurvey) => {
    setSelectedId(survey.id);
    setDeleteError("");
    setDeleteTarget(true);
  };

  const remove = async () => {
    if (!selected || !deleteTarget) return;
    const wasOnlySurveyOnPage = surveys.data?.data.length === 1;
    setDeleteError("");
    try {
      await deleteSurvey.mutateAsync(selected.id);
      setSelectedId("");
      setDetailsOpen(false);
      if (wasOnlySurveyOnPage && page > 1) setPage(page - 1);
      setDeleteTarget(false);
    } catch (cause) {
      setDeleteError(
        requestError(cause, "Could not delete this item. Try again."),
      );
    }
  };

  const columns = massTortSurveyTableColumns({
    onView: openSurvey,
    onDistribute: canDistribute ? openDistribution : undefined,
  });

  return (
    <AppPageShell padding="none" className="h-full min-h-0 overflow-auto">
      <div className="p-4 sm:p-6">
        <Card className="h-min">
          <CardContent>
            <div className="mt-4 mb-3 flex h-10 items-center gap-x-2">
              <Text variant="body" color="primary" className="font-semibold">
                Mass tort surveys
              </Text>
              {canCreate ? (
                <Button
                  variant="outline"
                  className="ml-auto flex"
                  onClick={() => {
                    setEditingDetails(false);
                    setCountBeforeCreate(
                      surveys.data?.pagination?.count ??
                        surveys.data?.data.length ??
                        0,
                    );
                    setDetailsOpen(true);
                  }}
                >
                  <PlusCircle className="size-4" /> Create survey
                </Button>
              ) : null}
            </div>
            {surveys.isError ? (
              <div className="py-6 text-sm">
                <p className="text-destructive">
                  Could not load mass tort surveys.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => void surveys.refetch()}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div className="min-h-64">
                <DataTable
                  columns={columns}
                  data={surveys.data?.data ?? []}
                  isLoading={surveys.isPending}
                  onRowClick={(row) => {
                    openSurvey(row.original);
                  }}
                  renderRowContextMenu={(row, trigger) => (
                    <ContextMenu
                      entityName="survey"
                      trigger={trigger}
                      open={{
                        onSelect: () => {
                          openSurvey(row.original);
                        },
                      }}
                    >
                      {canDistribute ? (
                        <ContextMenuItem
                          onSelect={() => {
                            openDistribution(row.original);
                          }}
                        >
                          <Send className="size-4" />
                          Distribute survey
                        </ContextMenuItem>
                      ) : null}
                    </ContextMenu>
                  )}
                  bodyRowClassName="cursor-pointer"
                  tableName={tanstackTableNames.massTortSurveys}
                  noResultsMessage="No mass tort surveys found."
                  hideSearchBar
                  hideVisiibilityDropdown
                />
                {(surveys.data?.pagination?.count ?? 0) > 50 ? (
                  <div className="mt-3 flex items-center justify-end gap-2 text-sm">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page === 1}
                      onClick={() => {
                        setPage((current) => current - 1);
                      }}
                    >
                      Previous
                    </Button>
                    <span>Page {page}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={
                        page * 50 >= (surveys.data?.pagination?.count ?? 0)
                      }
                      onClick={() => {
                        setPage((current) => current + 1);
                      }}
                    >
                      Next
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {detailsOpen &&
      (!editingDetails ||
        (selected && !questions.isPending && !questions.isError)) ? (
        <MassTortSurveyDetailsModal
          key={editingDetails ? selected?.id : "new"}
          massTortId={massTortId}
          survey={editingDetails ? selected : undefined}
          savedQuestions={editingDetails ? questions.data?.data : undefined}
          savedSections={editingDetails ? questions.data?.sections : undefined}
          canEdit={!editingDetails || canUpdate}
          canDelete={editingDetails && canDelete}
          onDelete={() => {
            if (selected) confirmDelete(selected);
          }}
          onClose={() => {
            setDetailsOpen(false);
          }}
          onSaved={(survey) => {
            if (!editingDetails) {
              setPage(Math.max(1, Math.ceil((countBeforeCreate + 1) / 50)));
            }
            setSelectedId(survey.id);
            setDetailsOpen(false);
          }}
        />
      ) : null}
      {detailsOpen && editingDetails && questions.isPending ? (
        <AppModal
          open
          onOpenChange={setDetailsOpen}
          title="Edit survey"
          size="2xl"
        >
          <div
            role="status"
            aria-label="Loading survey questions"
            className="space-y-4 py-2"
          >
            <div className="bg-muted h-10 w-full animate-pulse rounded" />
            <div className="bg-muted h-24 w-full animate-pulse rounded" />
            <div className="bg-muted h-32 w-full animate-pulse rounded" />
          </div>
        </AppModal>
      ) : null}
      {detailsOpen && editingDetails && questions.isError ? (
        <AppModal
          open
          onOpenChange={setDetailsOpen}
          title="Edit survey"
          size="2xl"
        >
          <p role="alert" className="text-destructive text-sm">
            Could not load the survey questions.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            onClick={() => void questions.refetch()}
          >
            Retry
          </Button>
        </AppModal>
      ) : null}
      {distributionOpen &&
      selected &&
      !questions.isPending &&
      !questions.isError ? (
        <DistributionPanel
          key={selected.id}
          survey={selected}
          canDistribute={canDistribute && Boolean(questions.data.data.length)}
          open={distributionOpen}
          onOpenChange={setDistributionOpen}
        />
      ) : null}
      {distributionOpen && selected && questions.isPending ? (
        <AppModal
          open
          onOpenChange={setDistributionOpen}
          title="Distribute survey"
        >
          <div
            role="status"
            aria-label="Loading survey questions"
            className="space-y-3 py-2"
          >
            <div className="bg-muted h-4 w-2/3 animate-pulse rounded" />
            <div className="bg-muted h-10 w-full animate-pulse rounded" />
          </div>
        </AppModal>
      ) : null}
      {distributionOpen && selected && questions.isError ? (
        <AppModal
          open
          onOpenChange={setDistributionOpen}
          title="Distribute survey"
        >
          <p role="alert" className="text-destructive text-sm">
            Could not load survey questions.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            onClick={() => void questions.refetch()}
          >
            Retry
          </Button>
        </AppModal>
      ) : null}
      <AppModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deleteSurvey.isPending) {
            setDeleteTarget(false);
          }
        }}
        overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
        className="z-[109]"
        title="Delete mass tort survey?"
        description="This cannot be undone."
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteTarget(false);
              }}
            >
              Keep
            </Button>
            <Button
              variant="destructive"
              disabled={deleteSurvey.isPending}
              onClick={() => void remove()}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-sm">
          The source survey and its questions will be deleted. Matter surveys
          already created from it will remain.
        </p>
        {deleteError ? (
          <p role="alert" className="text-destructive mt-3 text-sm">
            {deleteError}
          </p>
        ) : null}
      </AppModal>
    </AppPageShell>
  );
}
